// EncuestasListado.tsx
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import useLists from 'utils/customHooks/useLists';

interface EncuestasCListadoProps {
  filters: any[];
}

type TableData = Array<string[]>;

const headers: string[] = [
  'Nombre Docente',
  'Curso',
  'Las instancias de intercambio clases están bien preparadas y organizadas',
  'Desarrolla los temas de forma clara y entendible',
  'Promueve la participacion y reflexion de sus estudiantes',
  'Usa ejemplos y casos relacionados con la vida real',
  'Tiene un trato cordial y amable',
  'Utiliza recursos variados como apoyo didáctico',
  'Realiza instancias de correcion para evacuar dudas y que el estudiante...',
  'Es accesible y esta dispuesto a ayudarme cuando lo solicito',
  'Cumple adecuadamente con la planificación semanal del curso (horario)',
  'Cuán satisfecho/a estas con la actuación del docente',
  'Total de estudiantes que respondieron.',
  'Total de estudiantes inscriptos a la UC.'
];

const commentHeaders: string[] = ['Nombre Docente', 'Comentario'];

const normalizeKey = (s: string) =>
  s.normalize('NFD').replace(/\u0300-\u036f/g, '').toLowerCase().trim();

const buildDataGridRows = (data: TableData) =>
  data
    .slice(1)
    .map((row, index) => ({
      id: index + 1,
      docente: String(row[1] || '').trim(),
      values: row
    }))
    .sort((a, b) => a.docente.localeCompare(b.docente));

const buildCommentRows = (data: TableData) => {
  const map: Record<string, Set<string>> = {};
  data.slice(1).forEach(row => {
    const docente = String(row[0] || '').trim().replace(/(.+?)\1+/, '$1');
    const texto = String(row[1] || '').trim();
    if (!map[docente]) map[docente] = new Set();
    texto.split(/\.(?!\d)/)
      .map(t => t.trim())
      .filter(Boolean)
      .forEach(t => {
        const limpio = t.toLowerCase().includes('pajera')
          ? '• Comentario omitido por agravio.'
          : `• ${t}.`;
        map[docente].add(limpio);
      });
  });
  return Object.entries(map).map(([doc, comentarios], idx) => ({
    id: idx + 1,
    docente: doc,
    comentarios: Array.from(comentarios)
  }));
};

const downloadCSV = (rows: any[][], headers: string[], filename: string) => {
  const allRows = [headers, ...rows];
  const csv = allRows
    .map(r => r.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
};

const EncuestasCListado: React.FC<EncuestasCListadoProps> = ({ filters }) => {
  // Asegurar que 'filters' es siempre un array
  const safeFilters = Array.isArray(filters) ? filters : [];

  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [showUI, setShowUI] = useState(false);
  const [visibleComments, setVisibleComments] = useState<string | null>(null);

  const { users: a1, isLoadingList: l1 } = useLists('/v1.1/encuestascoordinador/listado1S', filters);
  const { users: a2, isLoadingList: l2 } = useLists('/v1.1/encuestascoordinador/listado2S', filters);
  const { users: c1 } = useLists('/v1.1/encuestascoordinador/comentariosDocentes1S', filters);
  const { users: c2 } = useLists('/v1.1/encuestascoordinador/comentariosDocentes2S', filters);

  useEffect(() => {
    const allLoaded = !l1 && !l2;
    setLoading(!allLoaded);
    if (allLoaded) setTimeout(() => setReady(true), 300);
  }, [l1, l2]);

  useEffect(() => {
    if (ready) setTimeout(() => setShowUI(true), 200);
  }, [ready]);

  // Extraer 'year' usando safeFilters
  const year = safeFilters.find((f: any) => f.codigo === 'year')?.valor;
  if (year === 2025) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No hay datos para los filtros seleccionados</p>
        <p>Por favor, seleccioná un año distinto a {year}.</p>
      </div>
    );
  }

  if (!showUI || loading) {
    return <div style={{ textAlign: 'center', marginTop: '2rem' }}>Cargando encuestas Docentes…</div>;
  }


  
  const data1 = a1 as unknown as TableData;
  const data2 = a2 as unknown as TableData;
  const comm1 = c1 as unknown as TableData;
  const comm2 = c2 as unknown as TableData;

  // Construir arreglo de semestres: 2S primero, luego 1S
  const surveys: Array<{ id: string; title: string; survey: TableData; comm: TableData }> = [];
  if ((Array.isArray(data2) && data2.length > 1) || (Array.isArray(comm2) && comm2.length > 1)) {
    surveys.push({ id: '2S', title: 'Encuestas Docentes 2S', survey: data2 as TableData, comm: comm2 as TableData });
  }
  if (Array.isArray(data1) && data1.length > 1) {
    surveys.push({ id: '1S', title: (data2.length <= 1 && comm2.length <= 1) ? 'Encuestas Anuales' : 'Encuestas Docentes 1S', survey: data1 as TableData, comm: comm1 as TableData });
  }

  return (
    <div style={{ padding: '2rem' }}>
      {surveys.map(({ id, title, survey, comm }) => {
        const rows = buildDataGridRows(survey);
        const comments = buildCommentRows(comm);
        const csvData = rows.map(r => r.values.slice(1));

        if (!rows.length) return null;
        return (
          <div key={id} style={{ marginBottom: '3rem' }}>
            <h2 style={{ textAlign: 'center' }}>{title}</h2>
            <div style={{ overflow: 'auto', maxHeight: 600, position: 'relative' }}>
              <table style={{ minWidth: 1200, borderCollapse: 'collapse', width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f1f1f1', zIndex: 1 }}>
                  <tr>
                    {headers.map((h, i) => (
                      <th
                        key={i}
                        style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left', fontWeight: 'bold' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx} style={{ background: idx % 2 ? '#f9f9f9' : '#fff' }}>
                      {r.values.slice(1, headers.length + 1).map((v, i) => (
                        <td key={i} style={{ border: '1px solid #ddd', padding: 8 }}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button
                onClick={() => setVisibleComments(id)}
                style={{ padding: '0.5rem 1rem', background: '#00AEEF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                Ver Comentarios
              </button>
              <div>
                <button
                  onClick={() => downloadCSV(csvData, headers, `encuestas_${id}.csv`)}
                  style={{ padding: '0.5rem 1rem', background: '#00AEEF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 8 }}>
                  Exportar Encuesta CSV
                </button>
                <button
                  onClick={() => {
                    const commAoA = buildCommentRows(comm).map(c => [c.docente, ...c.comentarios]);
                    downloadCSV(commAoA, commentHeaders, `comentarios_${id}.csv`);
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#00AEEF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  Exportar Comentarios CSV
                </button>
              </div>
            </div>
            {visibleComments === id && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
                <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, width: '64%', maxHeight: '64%', overflowY: 'auto' }}>
                  <h3>Comentarios {title}</h3>
                  {comments.map(c => (
                    <div key={c.id} style={{ marginBottom: '1rem' }}>
                      <strong>{c.docente.split(/\s+/).join(' ')}</strong>
                      <ul style={{ marginTop: 4 }}>
                        {c.comentarios.map((text, i) => (
                          <li key={i}>{text}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div style={{ textAlign: 'right' }}>
                    <button onClick={() => setVisibleComments(null)} style={{ padding: '0.5rem 1rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default connect((state: any) => ({ filters: state.filters }))(EncuestasCListado);
